<script setup lang="ts">
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { TableFilterCondition } from "../../../types/TableFilter.types";
import VInput from "../../Input/VInput.vue";
import FilterConditionSelect from "./FilterConditionSelect.vue";

interface Props {
  modelValue: string | null
  condition: TableFilterCondition
  placeholder?: string
  /** Id of the element that names the filter; its controls are labelled by it. */
  labelId?: string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: "",
  labelId: undefined,
});

const emit = defineEmits<{
  "update:modelValue": [value: string | null]
  "update:condition": [condition: TableFilterCondition]
}>();

const { t } = useI18n();

const conditionOptions = [
  { value: TableFilterCondition.CONTAINS, name: t("filters.conditions.contains") },
  { value: TableFilterCondition.EQUALS, name: t("filters.conditions.equals") },
  { value: TableFilterCondition.NOT_EQUALS, name: t("filters.conditions.not_equals") },
  { value: TableFilterCondition.STARTS_WITH, name: t("filters.conditions.starts_with") },
  { value: TableFilterCondition.ENDS_WITH, name: t("filters.conditions.ends_with") },
];

const localValue = ref(props.modelValue);
const localCondition = ref(props.condition);

watch(() => props.modelValue, (val) => {
  localValue.value = val;
});
watch(() => props.condition, (val) => {
  localCondition.value = val;
});

function updateValue(value: string | null) {
  localValue.value = value;
  emit("update:modelValue", value);
}

function updateCondition(condition: TableFilterCondition) {
  localCondition.value = condition;
  emit("update:condition", condition);
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <FilterConditionSelect
      :model-value="localCondition"
      :options="conditionOptions"
      :label-id="labelId"
      @update:model-value="updateCondition"
    />
    <VInput
      :model-value="localValue"
      :aria-labelledby="labelId"
      :placeholder="placeholder"
      @update:model-value="(v) => updateValue(v as string | null)"
    />
  </div>
</template>
